/**
 * Окно заготовки пряника: пряник с макетом и файлами печати, у которого ещё нет
 * товара PP0 (вкладка «Без товара»). Тот же каркас, что у окна товара: шапка
 * «Пряник · заготовка» с артикулом-резервом, колонка с превью, полоса «Что
 * дальше» с главной кнопкой «Создать товар» и вкладки «Обзор» и «Производство».
 *
 * Создание товара — то же действие, что массовое «Создать товары» вкладки
 * (фоновая задача, на маркетплейсы ничего не выгружается), для одного пряника.
 * Запуск и ход задачи ведёт вкладка, окно получает готовые `creating` и `onCreate`.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Cookie, Download, PackagePlus } from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { CopyButton } from '../../../components/ui/CopyButton'
import { NextStepBar } from '../../../components/ui/NextStepBar'
import { UnderlineTabs } from '../../../components/ui/UnderlineTabs'
import { EntityModalShell } from '../../../components/entity-view/EntityModalShell'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useToastStore } from '../../../stores/toastStore'
import { gingerbreadStudioAPI } from '../../gingerbread-studio/api'
import { formatSizeMm, formatWeight } from '../../gingerbread-studio/format'
import { CardSidebar, type CardSidebarStat } from '../../marketplace-catalog/components/CardSidebar'
import { Row, SectionTitle } from '../../products-v2/components/product-window/parts'
import { formatDate } from '../../products-v2/lib'
import { jobHref } from '../lib'
import type { NoProductRow } from '../types'

type BlankTab = 'overview' | 'production'

const NO_DATA = 'нет данных'
const need = 'Нужно право Gingerbread:APPROVE'

const shapeText = (row: NoProductRow): string | null =>
  row.shapeMode === 'unified' ? row.shapeName || 'Справочник' : row.shapeMode === 'free' ? 'Свободная' : null

const dash = (value: string | null | undefined) =>
  value ? value : <span className="text-gray-400 dark:text-zinc-500">{NO_DATA}</span>

interface Props {
  row: NoProductRow
  previewUrl: string
  /** Gingerbread:APPROVE: без права «Создать товар» неактивна и объясняет почему. */
  canApprove: boolean
  /** Задача создания товара уже идёт (запущена из этого окна или из вкладки). */
  creating: boolean
  /** Подтверждённое создание товара; ошибки показывает вызывающий. */
  onCreate: (row: NoProductRow) => Promise<void> | void
  onClose: () => void
}

export function GingerbreadBlankWindow({ row, previewUrl, canApprove, creating, onCreate, onClose }: Props) {
  const showToast = useToastStore((state) => state.showToast)
  const [tab, setTab] = useState<BlankTab>('overview')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [starting, setStarting] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const reserved = row.articleReserved
  const fileBase = reserved || row.designCode || row.gingerbreadId
  const title = row.name || (row.designCode ? `Пряник, макет ${row.designCode}` : 'Пряник без названия')

  const confirmCreate = async () => {
    setStarting(true)
    try {
      await onCreate(row)
      setConfirmOpen(false)
    } finally {
      setStarting(false)
    }
  }

  const downloadPrint = async () => {
    setDownloading(true)
    try {
      await gingerbreadStudioAPI.downloadFile(row.gingerbreadId, 'print_png', `${fileBase}.png`)
    } catch {
      showToast('Не удалось скачать файл печати', 'error')
    } finally {
      setDownloading(false)
    }
  }

  const stats: CardSidebarStat[] = [
    row.widthMm != null && row.heightMm != null
      ? { label: 'Размер', value: formatSizeMm(row.widthMm, row.heightMm) }
      : { label: 'Размер', value: NO_DATA, tone: 'dim', note: 'контур не измерен' },
    row.doughWeightG != null
      ? { label: 'Вес теста', value: formatWeight(row.doughWeightG) }
      : { label: 'Вес теста', value: NO_DATA, tone: 'dim', note: 'вес не посчитан' },
  ]

  const tabs = [
    { key: 'overview' as const, label: 'Обзор' },
    { key: 'production' as const, label: 'Производство' },
  ]

  return (
    <>
      <EntityModalShell
        isOpen
        ariaLabel={`Заготовка пряника ${reserved || row.gingerbreadId}`}
        suspendHotkeys={confirmOpen}
        kind={{ label: 'Пряник · заготовка', icon: Cookie }}
        primaryId={{ value: reserved || title, label: reserved ? 'артикул-резерв' : 'название' }}
        badges={<Badge variant="gray">Товара нет</Badge>}
        title={title}
        meta={[
          { label: 'Код макета', value: row.designCode, mono: true, copy: true },
          { label: 'Форма', value: shapeText(row) },
          { label: 'Задание', value: row.jobName },
        ]}
        keys={[]}
        activeKey={null}
        onNavigate={() => undefined}
        onClose={onClose}
        allowFullscreen
        nextStep={
          <NextStepBar
            tone="warn"
            title="Товара в ПИМ ещё нет"
            hint={
              creating
                ? 'Товар создаётся в фоне: после завершения заготовка станет товаром.'
                : 'Создайте товар PP0: он получит вес теста и файлы печати. На маркетплейсы ничего не выгружается.'
            }
            actions={[
              {
                key: 'create',
                label: 'Создать товар',
                icon: <PackagePlus className="h-4 w-4" />,
                onClick: () => setConfirmOpen(true),
                loading: creating,
                disabledReason: canApprove ? (creating ? 'Товар уже создаётся' : undefined) : need,
              },
            ]}
            checks={[
              row.designCode
                ? { key: 'design', label: 'Макет', value: `#${row.designCode}`, tone: 'ok' }
                : { key: 'design', label: 'Макет', tone: 'none', title: 'код макета не записан' },
              { key: 'product', label: 'Товар', value: 'нет', tone: 'warn' },
              reserved
                ? { key: 'article', label: 'Артикул', value: reserved, tone: 'info', title: 'резерв: под ним лежат файлы печати' }
                : { key: 'article', label: 'Артикул', tone: 'none', title: 'артикул-резерв не назначен' },
            ]}
          />
        }
        sidebar={
          <CardSidebar
            aspect="square"
            images={[previewUrl]}
            activeImage={0}
            onActiveImage={() => undefined}
            onOpenImage={() => undefined}
            emptyImageText="Нет превью"
            renderImage={(src, alt, className) => (
              <AuthorizedMediaImage src={src} alt={alt} thumbnailWidth={640} className={`${className} bg-white`} />
            )}
            stats={stats}
            originsTitle="Идентификаторы"
            origins={[
              ...(reserved ? [{ label: 'Артикул-резерв', value: reserved, mono: true, copy: true }] : []),
              ...(row.designCode ? [{ label: 'Макет', value: `#${row.designCode}`, mono: true }] : []),
              { label: 'Пряник', value: row.gingerbreadId, mono: true, copy: true },
            ]}
          />
        }
        tabsHotkeys={{ keys: tabs.map((item) => item.key), onSelect: (key) => setTab(key as BlankTab) }}
        tabs={
          <UnderlineTabs<BlankTab>
            tabs={tabs}
            active={tab}
            onChange={setTab}
            ariaLabel="Разделы заготовки пряника"
          />
        }
        footer={
          <>
            <span className="text-xs text-gray-500 dark:text-zinc-400">Заготовка: правок здесь нет</span>
            <span className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={onClose}>
                Закрыть
              </Button>
            </span>
          </>
        }
      >
        {tab === 'overview' ? (
          <div className="space-y-5">
            <section>
              <SectionTitle>Пряник</SectionTitle>
              <Row label="Название">{dash(row.name)}</Row>
              <Row label="Код макета">
                {row.designCode ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="font-mono">{row.designCode}</span>
                    <CopyButton value={row.designCode} label="код макета" size="xs" />
                  </span>
                ) : (
                  dash(null)
                )}
              </Row>
              <Row label="Будущий артикул">
                {reserved ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="font-mono">{reserved}</span>
                    <CopyButton value={reserved} label="артикул" size="xs" />
                  </span>
                ) : (
                  dash(null)
                )}
              </Row>
              <Row label="Форма">{dash(shapeText(row))}</Row>
              <Row label="Размер">{dash(row.widthMm != null && row.heightMm != null ? formatSizeMm(row.widthMm, row.heightMm) : null)}</Row>
              <Row label="Вес теста">{dash(row.doughWeightG != null ? formatWeight(row.doughWeightG) : null)}</Row>
              <Row label="Текст на изображении">{dash(row.textOnImage)}</Row>
              <Row label="Создан">{dash(row.createdAt ? formatDate(row.createdAt) : null)}</Row>
            </section>
            <section>
              <SectionTitle>Что будет при создании товара</SectionTitle>
              <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-zinc-300">
                <li>Появится товар PP0 с артикулом-резервом, весом теста и файлами печати.</li>
                <li>Карточки и черновики на площадках не создаются: это отдельный шаг для готового товара.</li>
                <li>Заготовка исчезнет из вкладки «Без товара».</li>
              </ul>
            </section>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="blank-production">
            <section>
              <SectionTitle>Файлы печати</SectionTitle>
              <p className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
                Файлы лежат в хранилище под {reserved ? <>артикулом <span className="font-mono">{reserved}</span></> : 'номером пряника'}.
              </p>
              <Button
                size="sm"
                variant="secondary"
                icon={<Download className="h-3.5 w-3.5" />}
                loading={downloading}
                onClick={() => void downloadPrint()}
              >
                Скачать печать (PNG)
              </Button>
            </section>
            <section>
              <SectionTitle>Задание генератора</SectionTitle>
              {row.jobId ? (
                <Link
                  to={jobHref(row.jobId)}
                  className="text-sm text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  {row.jobName || 'Открыть задание'}
                </Link>
              ) : (
                <span className="text-sm text-gray-400 dark:text-zinc-500">{NO_DATA}</span>
              )}
            </section>
          </div>
        )}
      </EntityModalShell>

      <ConfirmDialog
        isOpen={confirmOpen}
        layer="nested"
        title="Создать товар"
        message={`Для пряника «${title}» будет создан товар PP0: с весом теста и файлами печати. На маркетплейсы ничего не выгружается.`}
        confirmLabel="Создать товар"
        loading={starting}
        onConfirm={() => void confirmCreate()}
        onClose={() => {
          if (!starting) setConfirmOpen(false)
        }}
      />
    </>
  )
}
